import {
  ClockStationBlocksDto,
  ClockStationService,
  ControlBlocksService,
  DevicesClockSyncSourceModel,
  Devices_SyncDeviceParameterModel,
  DevicesService,
  DevicesTimeSynchronizationSource,
  Devices_UpdateArticleModel,
  Devices_UpdateModelModel,
  GetDevice_GetDeviceResponse,
  GetDeviceEvents_GetDeviceEventsResponse,
  GetDeviceStoredEvents_GetDeviceStoredEventsResponse,
  GetDevices_GetDevicesResponse,
  LogsService,
  MeteoDashboardService,
  SetDeviceSourceModel,
  StandardTimeModel,
  StandardTimeOffsetModel,
  StateModel,
  SyncDeviceParameter_SyncDeviceParameterType,
  UpdateDevicePlaceModel,
  UpdatePrivateDescriptionModel,
  GetControlBlockStatusBatch_GetControlBlockStatusBatchResponse,
  GetMeteoDashboardData_GetMeteoDashboardDataResponse,
} from "../api";
import {BlockState} from "../components/menu/BlocksControl/BlocksControl.model";
import {
  mapTimeValue,
} from "../shared/Helpers.utils";

export const SharedApi = {

  // Получение устройств
  getDevices: async (id: string): Promise<GetDevices_GetDevicesResponse> => {
    return ControlBlocksService.getApiControlBlocksDevices(id);
  },

  // Перезагрузка устройства
  rebootDevice: async (id: string): Promise<boolean> => {
    await DevicesService.postApiDevicesReboot(id);
    return true;
  },

  // Загрузка прошивки внешнего устройства
  uploadDeviceFirmware: async (id: string, file: Blob): Promise<boolean> => {
    await DevicesService.postApiDevicesFirmwareUpload(id, {file});
    return true;
  },

  // Получение информации об устройстве
  getDeviceInfo: async (id: string): Promise<GetDevice_GetDeviceResponse> => {
    return DevicesService.getApiDevices(id);
  },

  // Чтение параметра с физического устройства и сохранение в БД
  syncDeviceParameter: async (
    id: string,
    parameter: SyncDeviceParameter_SyncDeviceParameterType
  ): Promise<boolean> => {
    const payload: Devices_SyncDeviceParameterModel = {parameter};
    await DevicesService.postApiDevicesParametersSync(id, payload);
    return true;
  },

  // Перезагрузка блока управления
  rebootControlBlocks: async (id: string): Promise<boolean> => {
    await ControlBlocksService.postApiControlBlocksReboot(id);
    return true;
  },

  // Синхронизация времени
  syncDeviceTime: async (id: string, syncSource: DevicesTimeSynchronizationSource): Promise<boolean> => {
    const payload: DevicesClockSyncSourceModel = {syncSource};
    await ControlBlocksService.postApiControlBlocksDevicesTimeSyncSource(id, payload);
    return true;
  },

  // Обновление места установки
  updatePlace: async (id: string, place: string): Promise<boolean> => {
    const payload: UpdateDevicePlaceModel = {place};
    await DevicesService.putApiDevicesPlace(id, payload);
    return true;
  },

  // Обновление артикула
  updateArticle: async (id: string, article: string): Promise<boolean> => {
    const payload: Devices_UpdateArticleModel = {article};
    await DevicesService.putApiDevicesArticle(id, payload);
    return true;
  },

  // Обновление модели
  updateModel: async (id: string, model: string): Promise<boolean> => {
    const payload: Devices_UpdateModelModel = {model};
    await DevicesService.putApiDevicesModel(id, payload);
    return true;
  },

  // Обновление приватного описания
  updatePrivateDescription: async (id: string, privateDescription: string): Promise<boolean> => {
    const payload: UpdatePrivateDescriptionModel = {privateDescription};
    await DevicesService.putApiDevicesDescriptionPrivate(id, payload);
    return true;
  },

  // Видимость устройств на главной странице
  changeVisibility: async (id: string, enabled: boolean): Promise<boolean> => {
    const payload: StateModel = {enabled};
    await DevicesService.postApiDevicesVisibility(id, payload);
    return true;
  },

  // Обновление времени
  updateUTCTime: async (id: string, time: string | Date): Promise<boolean> => {
    const payload: StandardTimeModel = {time: mapTimeValue(time)};
    await DevicesService.postApiDevicesTimeUtc(id, payload);
    return true;
  },

  // Обновление часового пояса
  updateTimezone: async (id: string, offset: string): Promise<boolean> => {
    const payload = {
      offset,
    } as unknown as StandardTimeOffsetModel;

    await DevicesService.postApiDevicesTimeTimezone(id, payload);
    return true;
  },

  // Обновление автоперехода зима / лето
  updateDaylightSaving: async (id: string, enabled: boolean): Promise<boolean> => {
    const payload: StateModel = {enabled};
    await DevicesService.postApiDevicesTimeDaylightSaving(id, payload);
    return true;
  },

  // Получение источника синхронизации
  getSyncSource: async (id: string) => {
    return ControlBlocksService.getApiControlBlocksDevicesTimeSynchronizationSource(id);
  },

  // Выбор источника синхронизации
  updateSyncSource: async (id: string, syncSource: DevicesTimeSynchronizationSource): Promise<boolean> => {
    const payload: DevicesClockSyncSourceModel = {syncSource};
    await ControlBlocksService.postApiControlBlocksDevicesTimeSynchronizationSource(id, payload);
    return true;
  },

  // Синхронизация времени
  timeSyncNow: async (id: string): Promise<boolean> => {
    await ControlBlocksService.postApiControlBlocksDevicesTimeSyncNow(id);
    return true;
  },

  // Получение времени
  getTime: async (id: string) => {
    return DevicesService.getApiDevicesTime(id);
  },


  // Получение стейта Bluetooth
  getBluetoothState: async (id: string) => {
    const response = await ControlBlocksService.getApiControlBlocksBluetoothState(id);

    return {
      ...response,
      state: response.state ?? false,
      enabled: response.enabled ?? false,
    };
  },

  // Обновление стейта Bluetooth
  updateBluetoothState: async (id: string ,enabled: boolean): Promise<boolean> => {
    const payload: StateModel = {enabled};
    await ControlBlocksService.postApiControlBlocksBluetoothState(id, payload);
    return true;
  },

  // Получение стейта прошивки
  getFirmwareState: async (id: string) => {
    const response = await ControlBlocksService.getApiControlBlocksFirmwareState(id);

    return {
      ...response,
      allowEspUpdate: response.allowEspUpdate ?? false,
      release: response.release ?? false,
    };
  },

  // Обновление прошивки
  updateReleaseFirmwareState: async (id: string , enabled: boolean): Promise<boolean> => {
    const payload: StateModel = {enabled};
    await ControlBlocksService.postApiControlBlocksFirmwareRelease(id, payload);
    return true;
  },

  // Обновление авто-обновления
  updateAutoUpdate: async (id: string , enabled: boolean): Promise<boolean> => {
    const payload: StateModel = {enabled};
    await ControlBlocksService.postApiControlBlocksFirmwareUpdateAuto(id, payload);
    return true;
  },

  // Получение метео данных
  getMeteoData: async (id: string): Promise<GetMeteoDashboardData_GetMeteoDashboardDataResponse> => {
    return MeteoDashboardService.getApiMeteoDashboardControlBlock(id);
  },

  // Обновление метео данных
  updateMeteoDataDevice: async (id: string , deviceId: string): Promise<boolean> => {
    const payload: SetDeviceSourceModel = {deviceId};
    await MeteoDashboardService.putApiMeteoDashboardControlBlockDevice(id, payload);
    return true;
  },

  // Получение логов
  getDeviceLogs: async (id: string) => {
    return LogsService.getApiLogs(id);
  },

  // Получение блокировок
  getBlocks: async (id: string) => {
    const response = await ClockStationService.getApiClockStationBlocks(id);

    return {
      scheduleLock: response.scheduleLock ?? false,
      astronomicalRelayLock: response.astronomicalRelayLock ?? false,
      timeRelaysLock: response.timeRelaysLock ?? false,
      syncLock: response.syncLock ?? false,
      channelLocks: response.channelLocks ?? [],
      newYearChimesLock: response.newYearChimesLock ?? false,
      clockChimesLock: response.clockChimesLock ?? false,
    };
  },


  // Обновление блокировок
  updateBlocks: async (id: string , blocks: BlockState): Promise<boolean> => {
    await ClockStationService.postApiClockStationBlocks(id, blocks as unknown as ClockStationBlocksDto);
    return true;
  },

  // Получение статуса устройства
  getControlBlocksStatus: async (id: string): Promise<GetControlBlockStatusBatch_GetControlBlockStatusBatchResponse> => {
    return ControlBlocksService.getApiControlBlocksStatus(id);
  },

  // Получение статусов устройств
  getControlBlocksStatusBatch: async (controlBlockIds: string[]): Promise<GetControlBlockStatusBatch_GetControlBlockStatusBatchResponse[]> => {
    if (!controlBlockIds || controlBlockIds.length === 0) {
      return [];
    }

    return ControlBlocksService.postApiControlBlocksStatusBatch({controlBlockIds});
  },

  // Получение устройств
  getControlBlocks: async () => {
    return ControlBlocksService.getApiControlBlocks();
  },

  // Загрузка превью устройства
  uploadControlBlockPreview: async (id: string, photo: Blob): Promise<string> => {
    return ControlBlocksService.postApiControlBlocksPreview(id, {photo});
  },

  // Загрузка прошивки устройства
  uploadControlBlockFirmware: async (id: string, file: Blob): Promise<boolean> => {
    await ControlBlocksService.postApiControlBlocksFirmwareUpload(id, {file});
    return true;
  },

  // Получение информации об устройстве
  getControlBlockInfo: async (id: string) => {
    return ControlBlocksService.getApiControlBlocks1(id);
  },

  // Получение сохранённых событий устройства
  getDeviceStoredEvents: async (id: string): Promise<GetDeviceStoredEvents_GetDeviceStoredEventsResponse> => {
    return DevicesService.getApiDevicesEventsStored(id);
  },

  // Получение событий устройства
  getDeviceEvents: async (
    id: string,
    after?: string,
    dateFrom?: string,
    dateTo?: string,
    pageSize?: number
  ): Promise<GetDeviceEvents_GetDeviceEventsResponse> => {
    return DevicesService.getApiDevicesEvents(id, after, dateFrom, dateTo, pageSize);
  },

  // Сброс сохранённых событий устройства
  resetStoredEvents: async (id: string): Promise<boolean> => {
    await DevicesService.postApiDevicesEventsStoredReset(id);
    return true;
  },

}
